/**
 * API response types
 */

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  requestId?: string;
}

export type SubscriptionCallback = (data: any) => void;

// ============================================================================
// BUCKET TYPES
// ============================================================================

/**
 * Raw bucket data structure as returned from the API
 * All numeric values are strings in satoshi format
 * Field names are in snake_case format
 *
 * This matches the actual API response format from /bucket/v1/address/{address}/buckets
 */
export interface RawBucket {
  bucket_id: string;
  address: string;
  margin_type: string; // e.g., "cross", "isolated"
  collateral_asset: string; // e.g., "USDC"
  deposited_margin: string; // Satoshi format (string) - total margin deposited
  total_equity: string; // Satoshi format (string)
  available_margin: string; // Satoshi format (string)
  used_margin: string; // Satoshi format (string)
  unrealized_profit: string; // Satoshi format (string)
  unrealized_loss: string; // Satoshi format (string)
  realized_profit: string; // Satoshi format (string)
  realized_loss: string; // Satoshi format (string)
  margin_ratio: string; // Satoshi format (string) - margin ratio
  imr: string; // Initial Margin Requirement (as string, e.g., "10" for 10%)
  total_notional_value: string; // Satoshi format (string)
  risk_level: string; // e.g., "low", "medium", "high"
  max_leverage: string | number; // Can be string "20" or number
  correlation_offset: number; // Correlation offset (number)
  status: string; // e.g., "dormant", "active"
  created_at: string | Timestamp; // ISO timestamp string OR protobuf Timestamp object { seconds, nanos }
  updated_at: string | Timestamp; // ISO timestamp string OR protobuf Timestamp object { seconds, nanos }
  sequence_id?: string; // Sequence ID (optional, may not be present in all responses)
}

/**
 * WebSocket buckets update message structure
 * Format: { buckets: RawBucket[], total_buckets: number, address: string, timestamp: number }
 */
export interface BucketsUpdateMessage {
  buckets: RawBucket[];
  total_buckets: number;
  address: string;
  timestamp: number;
  [key: string]: any; // Allow additional fields
}

// ============================================================================
// MARKET TYPES
// ============================================================================

/**
 * Raw market data from API (snake_case format)
 * Matches actual API response structure from /market/all endpoint
 */
export interface RawMarket {
  market_id: string;
  moniker: string;
  base_asset: string;
  quote_asset: string;
  market_type: string; // e.g., "linear_perp"
  orderbook_type: string; // e.g., "clob"
  status: string; // e.g., "active"
  shard_id: string; // e.g., "shard_0"
  activated_at: string; // ISO date string, e.g., "2024-01-01T00:00:00Z"
  created_at: string; // ISO date string, e.g., "2024-01-01T00:00:00Z"
}

/**
 * Market response (can be array or object with markets array)
 */
export type MarketsResponse = RawMarket[] | { markets: RawMarket[] };

/**
 * Market statistics/ticker data (snake_case format)
 */
export interface RawMarketStats {
  market_id: string;
  symbol: string;
  moniker: string;
  last_price: string;
  bid_price: string;
  ask_price: string;
  volume?: string;
  high_24h?: string;
  low_24h?: string;
  price_change_24h?: string;
  price_change_percent_24h?: string;
  timestamp?: number;
}

/**
 * Exchange info response
 */
export interface ExchangeInfoResponse {
  timezone?: string;
  serverTime?: number;
  rateLimits?: Array<{
    rateLimitType?: string;
    interval?: string;
    intervalNum?: number;
    limit?: number;
  }>;
  exchangeFilters?: any[];
  symbols?: any[];
  [key: string]: any;
}

// ============================================================================
// ORDERBOOK TYPES
// ============================================================================

/**
 * Orderbook entry [price, size]
 * Can be strings (raw API format) or numbers (converted format)
 */
export type OrderbookEntry = [string | number, string | number];

/**
 * Orderbook response (snake_case format)
 */
export interface RawOrderbook {
  market_id: string;
  bids: OrderbookEntry[];
  asks: OrderbookEntry[];
  last_update_id: number;
  timestamp: number;
}

// ============================================================================
// TRADE TYPES
// ============================================================================

/**
 * Raw trade data from API (snake_case format)
 */
export interface RawTrade {
  trade_id: string;
  buy_order_id: string;
  sell_order_id: string;
  price: string; // Satoshi format
  quantity: string; // Satoshi format
  timestamp: string; // Timestamp as string
  market_id: string;
  maker_fee: string; // Satoshi format
  taker_fee: string; // Satoshi format
  dual_write_id: string;
  sequence_id: string;
  trade_type: string; // e.g., "INITIAL"
  buy_address: string;
  sell_address: string;
}

/**
 * Trades response structure from API
 * Matches actual REST API response format from /clob/v1/trades endpoint
 */
export interface TradesResponse {
  trades: RawTrade[];
  total_count: number;
  market_id: string;
  symbol: string;
  timestamp: Timestamp; // Protobuf Timestamp object { seconds, nanos }
}

/**
 * Aggregate trade data
 */
export interface RawAggTrade {
  symbol: string;
  price: string | number;
  quantity: string | number;
  first_trade_id: string | number;
  last_trade_id: string | number;
  timestamp: number;
  [key: string]: any;
}

// ============================================================================
// KLINE/CANDLESTICK TYPES
// ============================================================================

/**
 * Kline/candlestick data
 * Format: [openTime, open, high, low, close, volume, closeTime, quoteVolume, trades, ...]
 */
export type RawKline = [
  number, // openTime
  string | number, // open
  string | number, // high
  string | number, // low
  string | number, // close
  string | number, // volume
  number, // closeTime
  string, // quoteVolume
  number?, // trades
  ...any[],
];

/**
 * WebSocket kline data structure
 * Matches the actual WebSocket message format with protobuf timestamps
 */
export interface WebSocketKlineData {
  market_id: string;
  symbol?: string;
  interval: string;
  open: string; // Satoshi format
  high: string; // Satoshi format
  low: string; // Satoshi format
  close: string; // Satoshi format
  volume: string; // Satoshi format
  trade_count?: number;
  open_time: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  close_time: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  timestamp: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  [key: string]: any; // Allow additional fields
}

/**
 * Klines response (array of klines)
 */
export type KlinesResponse = RawKline[];

// ============================================================================
// POSITION TYPES
// ============================================================================

/**
 * Raw position data from API (snake_case format)
 * Matches actual API response structure from /bucket/v1/address/{address}/positions
 */
export interface RawPosition {
  position_id: string; // Position identifier
  bucket_id: string;
  market_id: string;
  address: string; // Wallet address
  symbol: string;
  size: string; // Satoshi format
  side: string; // Position side: "SIDE_BUY" (long) or "SIDE_SELL" (short)
  average_entry_price: string; // Satoshi format (average entry price)
  mark_price: string; // Satoshi format
  leverage: string; // Satoshi format (e.g., "200000000" = 2x)
  unrealized_profit: string; // Satoshi format
  unrealized_loss: string; // Satoshi format
  mmr: string; // Maintenance margin requirement (Satoshi format)
  margin_balance: string; // Satoshi format
  status: string; // Position status (e.g., "active")
  created_at: Timestamp; // ISO timestamp string
  updated_at: Timestamp; // ISO timestamp string
  // Legacy/optional fields for backward compatibility
  unrealized_pnl?: string; // Legacy field (calculate from unrealized_profit - unrealized_loss)
  liquidation_price?: string; // Satoshi format (optional, not always in response)
  market_type?: string; // Market type (optional, not always in response)
  margin_mode?: string; // Optional, not always in response
  margin_type?: string; // Optional, not always in response
}

/**
 * Positions response structure from API
 */
export interface PositionsResponse {
  success: boolean;
  error_message: string;
  positions: RawPosition[];
  total_count: number;
  filtered_count: number;
  was_filtered: boolean;
  shard_id: string;
}

// ============================================================================
// ORDER TYPES
// ============================================================================

/**
 * Order side union - strict types from API
 */
export type OrderSide = "SIDE_BUY" | "SIDE_SELL";

/**
 * Order type union - strict types from API
 */
export type OrderType =
  | "ORDER_TYPE_UNSPECIFIED"
  | "ORDER_TYPE_LIMIT"
  | "ORDER_TYPE_MARKET"
  | "ORDER_TYPE_STOP_LIMIT"
  | "ORDER_TYPE_STOP_MARKET";

/**
 * Order status union - strict types from API
 */
export type OrderStatus =
  | "ORDER_STATUS_OPEN"
  | "ORDER_STATUS_FILLED"
  | "ORDER_STATUS_CANCELLED"
  | "ORDER_STATUS_PARTIAL"
  | "ORDER_STATUS_PENDING";

/**
 * Raw order data from API (snake_case format)
 * Matches actual API response structure from orders endpoints
 */
export interface RawOrder {
  order_id: string;
  market_id: string;
  bucket_id: string;
  address: string;
  side: OrderSide;
  type: OrderType;
  category: string; // e.g., "MARKET_CATEGORY_LINEAR"
  price: string; // Satoshi format
  quantity: string; // Satoshi format
  status: OrderStatus; // Order status from API
  power: number;
  leverage: string;
  filled_quantity: string; // Satoshi format
  average_price: string; // Satoshi format
  take_profit: string; // Can be empty string
  stop_loss: string; // Can be empty string
  timestamp: Timestamp; // Timestamp as string
  time_in_force: string; // e.g., "TIME_IN_FORCE_GTC"
  post_only: boolean;
  hidden: boolean;
  display_quantity: string; // Can be empty string
  dual_write_id: string;
  sequence_id: string;
}

/**
 * Orders response (can be array or object with orders array and metadata)
 */
export interface OrdersResponse {
  orders: RawOrder[];
  total_count: number;
  timestamp:Timestamp| string | number;
  [key: string]: any;
}

// ============================================================================
// BALANCE TYPES
// ============================================================================

/**
 * Raw balance data from API (snake_case format)
 * Matches actual API response from /bank/v1/balances/{address}
 */
export interface RawBalance {
  asset: string; // Asset symbol (e.g., "MORM", "USDC")
  balance: string; // Total balance in satoshi format (string)
  available_balance: string; // Available balance in satoshi format (string)
  locked_balance: string; // Locked balance in satoshi format (string)
}

/**
 * Get balances response from /bank/v1/balances/{address}
 * Actual API response structure:
 * {
 *   "address": "0x...",
 *   "balances": [...],
 *   "shard_id": ""
 * }
 */
export interface GetBalancesResponse {
  address: string; // Wallet address
  balances: RawBalance[]; // Array of balance objects
  shard_id: string; // Shard ID (can be empty string)
}

// ============================================================================
// FUNDING RATE TYPES
// ============================================================================

/**
 * Raw funding rate data
 */
export interface RawFundingRate {
  symbol?: string;
  funding_rate?: string | number;
  funding_time?: number;
  mark_price?: string | number;
  [key: string]: any;
}

/**
 * Funding rate response
 */
export type FundingRateResponse =
  | RawFundingRate
  | RawFundingRate[]
  | { data?: RawFundingRate[] };

/**
 * Premium index data
 */
export interface RawPremiumIndex {
  symbol?: string;
  mark_price?: string | number;
  index_price?: string | number;
  estimated_settle_price?: string | number;
  last_funding_rate?: string | number;
  next_funding_time?: number;
  interest_rate?: string | number;
  [key: string]: any;
}

// ============================================================================
// HEALTH & METRICS TYPES
// ============================================================================

/**
 * Health summary response
 */
export interface HealthSummaryResponse {
  status?: string;
  overall?: string;
  components?: Record<
    string,
    {
      status?: string;
      message?: string;
      [key: string]: any;
    }
  >;
  timestamp: Timestamp | number;
  [key: string]: any;
}

/**
 * Nonce status response
 */
export interface NonceStatusResponse {
  address?: string;
  next_nonce?: number;
  nextNonce?: number;
  [key: string]: any;
}

/**
 * Server time response
 */
export interface ServerTimeResponse {
  serverTime?: number;
  time?: number;
  timestamp?: number;
  [key: string]: any;
}

/**
 * TPS metrics response
 */
export interface TPSMetricsResponse {
  current?: number;
  peak?: number;
  average?: number;
  [key: string]: any;
}

/**
 * Latency metrics response
 */
export interface LatencyMetricsResponse {
  p50?: number;
  p95?: number;
  p99?: number;
  avg?: number;
  max?: number;
  min?: number;
  [key: string]: any;
}

// ============================================================================
// OTHER TYPES
// ============================================================================

/**
 * Open interest response
 */
export interface OpenInterestResponse {
  symbol?: string;
  open_interest?: string | number;
  [key: string]: any;
}

/**
 * TWAP response
 */
export interface TWAPResponse {
  symbol?: string;
  twap?: string | number;
  duration?: number;
  [key: string]: any;
}

/**
 * Index info response
 */
export interface IndexInfoResponse {
  symbol?: string;
  index_price?: string | number;
  [key: string]: any;
}

/**
 * Asset index response
 */
export interface AssetIndexResponse {
  symbol?: string;
  asset_index?: string | number;
  [key: string]: any;
}

/**
 * Constituents response
 */
export interface ConstituentsResponse {
  symbol?: string;
  constituents?: Array<{
    asset?: string;
    weight?: string | number;
    [key: string]: any;
  }>;
  [key: string]: any;
}

/**
 * Delivery price response
 */
export interface DeliveryPriceResponse {
  pair?: string;
  delivery_price?: string | number;
  [key: string]: any;
}

/**
 * Basis data response
 */
export interface BasisResponse {
  symbol?: string;
  basis?: string | number;
  [key: string]: any;
}

/**
 * Long/short position ratio response
 */
export interface LongShortRatioResponse {
  symbol?: string;
  long_short_ratio?: string | number;
  [key: string]: any;
}

/**
 * Supported actions response
 */
export interface SupportedActionsResponse {
  actions?: string[];
  [key: string]: any;
}

/**
 * Validate action response
 */
export interface ValidateActionResponse {
  valid: boolean;
  action: string;
  message: string;
  [key: string]: any;
}

/**
 * Market data response
 */
export interface MarketDataResponse {
  asset_pair: string;
  [key: string]: any;
}

/**
 * Market candles response
 */
export type MarketCandlesResponse = RawKline[];

// ============================================================================
// EXCHANGE CLIENT RESPONSE TYPES
// ============================================================================

/**
 * Place order response from /clob/v1/order endpoint
 * Response structure for order submission
 *
 * Actual response format:
 * {
 *   "order_id": "a6e11e9221282158df809f3cc48852a37160ae17d51e1e0fc61c781df32dfb52",
 *   "success": true,
 *   "error_message": "",
 *   "timestamp": "2025-12-17T13:32:06.228706944Z"
 * }
 */
export interface PlaceOrderResponse {
  order_id: string;
  client_order_id?: string;
  timestamp: number; // ISO date string (e.g., "2025-12-17T13:32:06.228706944Z") or Unix timestamp
  error_message: string; // Error message if order submission failed
  success: boolean;
}

/**
 * Protobuf-style timestamp structure
 */
export interface Timestamp {
  seconds: number;
  nanos: number;
}

/**
 * Cancel order result structure
 * The result field in cancelOrder response
 */
export interface CancelOrderResult {
  order_id: string;
  success: boolean;
  timestamp: Timestamp;
}

/**
 * Router transaction response from /router/v1/tx endpoint
 * Common response structure for all router v1 transaction operations
 */
export interface RouterTxResponse {
  txhash: string;
  status?: string;
  message?: string;
  success?: boolean;
  error?: string;
  data?: any;
  result?: any; // Operation-specific result data
  timestamp?: Timestamp| number; // Top-level timestamp
  [key: string]: any;
}

/**
 * Cancel order response
 * Response from cancelOrder operation (uses /router/v1/tx)
 * Structure: { success, txhash, result: { order_id, success, timestamp }, timestamp }
 */
export interface CancelOrderResponse extends RouterTxResponse {
  success: boolean;
  txhash: string;
  result: CancelOrderResult;
  timestamp: Timestamp | number;
}

/**
 * Modify order response
 * Response from modifyOrder operation (uses /router/v1/tx)
 */
export type ModifyOrderResponse = RouterTxResponse;

/**
 * Change leverage response
 * Response from changeLeverage operation (uses /router/v1/tx)
 */
export type ChangeLeverageResponse = RouterTxResponse;

/**
 * Close position response
 * Response from closePosition operation (uses /router/v1/tx)
 * Matches the response structure from bucket/position_close_test.go:
 * { success: boolean, error_message: string }
 */
export interface ClosePositionResponse extends RouterTxResponse {
  success: boolean;
  error_message?: string; // Error message if position close failed (snake_case to match Go test)
  error?: string; // Alternative error field (camelCase)
  message?: string; // Alternative message field
}

/**
 * Swap response
 * Response from swap operation (uses /router/v1/tx)
 */
export type SwapResponse = RouterTxResponse;

/**
 * Change margin ratio response
 * Response from changeMarginRatio operation (uses /router/v1/tx)
 */
export type ChangeMarginRatioResponse = RouterTxResponse;

/**
 * Internal transfer response
 * Response from internTransfer operation (uses /router/v1/tx)
 */
export type InternTransferResponse = RouterTxResponse;

/**
 * Bank mint response
 * Response from bankMint operation (uses /router/v1/tx)
 */
export type BankMintResponse = RouterTxResponse;

/**
 * Bank transfer response
 * Response from bankTransfer operation (uses /router/v1/tx)
 */
export type BankTransferResponse = RouterTxResponse;

/**
 * Bucket transfer response from /bucket/v1/bucket/transfer endpoint
 * Response structure for bucket-to-bucket transfers
 */
export interface BucketTransferResponse {
  success: boolean;
  message: string;
  txhash: string; // Transaction hash
  transferred_amount: string; // Satoshi format
  timestamp: Timestamp|string; // Timestamp as protobuf Timestamp object or ISO string
  source_bucket_id: string;
  target_bucket_id: string;
  source_balance_after: string; // Satoshi format
  target_balance_after: string; // Satoshi format
  [key: string]: any;
}

/**
 * Extract to bucket response from /bank/v1/extract-to-bucket endpoint
 * Response structure for bank-to-bucket transfers
 */
export interface ExtractToBucketResponse {
  success: boolean;
  error_message: string;
  txhash: string;
  bucket_id: string;
  amount_transferred: string; // Satoshi format
  spot_balance_after: string; // Satoshi format
  bucket_margin_after: string; // Satoshi format
  timestamp: Timestamp|string; // ISO date string (e.g., "2025-12-17T13:32:07.474103840Z")
  [key: string]: any;
}

/**
 * Withdraw from bucket response from /bank/v1/withdraw-from-bucket endpoint
 * Response structure for bucket-to-bank transfers
 */
export interface WithdrawFromBucketResponse {
  success: boolean;
  error_message: string;
  txhash: string;
  bucket_id: string;
  amount_transferred: string; // Satoshi format
  spot_balance_after: string; // Satoshi format
  bucket_margin_after: string; // Satoshi format
  timestamp: Timestamp|string; // ISO date string (e.g., "2025-12-17T13:32:07.474103840Z")
  [key: string]: any;
}

/**
 * Create bucket response from /bucket/v1/bucket endpoint
 * Response structure for bucket creation
 */
export interface CreateBucketResponse {
  success: boolean;
  bucket_id: string;
  message: string;
  [key: string]: any;
}

// ============================================================================
// CONSENSUS FLAT TYPES
// ============================================================================

/**
 * Consensus item type union
 */
export type ConsensusItemType =
  | "vertex"
  | "tx"
  | "validator"
  | "unconfirmed_tx";

/**
 * Consensus event type union
 */
export type ConsensusEventType =
  | "new_vertex"
  | "new_tx"
  | "validator_update"
  | string;

/**
 * Common fields for all consensus item types (ConsensusCommonItemFlat)
 * These fields are present in all consensus items regardless of type.
 */
export interface ConsensusCommonItemFlat {
  id: string; // Unique identifier (e.g., hash for TX/vertex, ID for validator)
  type: ConsensusItemType; // Item type
  shard_id: number; // Shard identifier
  timestamp: number; // Unix timestamp
  status: string; // Status (e.g., "pending", "processed", "confirmed")
  original_proto_hash: string; // SHA256 hash of the original protobuf message
}

/**
 * Vertex-specific consensus item (ConsensusVertexItemFlat)
 * Represents a DAG vertex/block in the consensus system.
 */
export interface ConsensusVertexItemFlat extends ConsensusCommonItemFlat {
  type: "vertex";
  parent_ids?: string[]; // Parent vertex hashes
  tx_count?: number; // Number of transactions in the vertex
  state_root?: string; // State root hash
  qc_achieved?: boolean; // Whether quorum certificate was achieved
  qc_vote_count?: number; // Number of QC votes
  qc_agg_signature?: string; // Base64-encoded aggregate QC signature
}

/**
 * Transaction-specific consensus item (ConsensusTxItemFlat)
 * Represents a confirmed transaction in the consensus system.
 */
export interface ConsensusTxItemFlat extends ConsensusCommonItemFlat {
  type: "tx";
  from_address?: string; // Sender address
  to_address?: string; // Recipient address
  amount?: string; // Transfer amount (as string for precision)
  tx_type?: string; // Transaction type (e.g., "transfer", "contract_call")
  txhash?: string; // Transaction hash
  nonce?: number; // Nonce value
}

/**
 * Unconfirmed transaction-specific consensus item (ConsensusUnconfirmedTxItemFlat)
 * Represents an unconfirmed transaction in the consensus system.
 */
export interface ConsensusUnconfirmedTxItemFlat
  extends ConsensusCommonItemFlat {
  type: "unconfirmed_tx";
  from_address?: string; // Sender address
  to_address?: string; // Recipient address
  amount?: string; // Transfer amount (as string for precision)
  tx_type?: string; // Transaction type (e.g., "transfer", "contract_call")
  txhash?: string; // Transaction hash
  nonce?: number; // Nonce value
}

/**
 * Validator-specific consensus item (ConsensusValidatorItemFlat)
 * Represents a validator in the consensus system.
 */
export interface ConsensusValidatorItemFlat extends ConsensusCommonItemFlat {
  type: "validator";
  voting_power?: number; // Validator's voting power
  public_key?: string; // Public key (hex-encoded)
  validator_status?: string; // Status (e.g., "active", "inactive", "slashed")
  validator_id?: string; // Validator ID
}

/**
 * Union type for all consensus item types (ConsensusItemFlat)
 * This is a discriminated union based on the `type` field, allowing TypeScript
 * to narrow the type based on the `type` property.
 */
export type ConsensusItemFlat =
  | ConsensusVertexItemFlat
  | ConsensusTxItemFlat
  | ConsensusUnconfirmedTxItemFlat
  | ConsensusValidatorItemFlat;

/**
 * Consensus event flat structure (ConsensusEventFlat)
 * Flattened (linearized) consensus events from Morpheum's DAG-based consensus system.
 * This is designed for easier consumption by block explorers and clients, converting
 * complex DAG structures into a simpler, block-like event stream.
 *
 * The payload follows the `ConsensusEventFlat` Go struct (from `services/consensus/types.go`),
 * which is marshaled to JSON using `protojson` (snake_case fields, no zero values emitted).
 */
export interface ConsensusEventFlat {
  event_type: ConsensusEventType; // Type of event (e.g., "new_vertex", "new_tx", "validator_update")
  data: ConsensusItemFlat; // Flattened details of the event (discriminated union)
  source_shard: string; // Shard ID where the event originated
  event_time: number; // Unix timestamp of the event
}

// ============================================================================
// TRANSACTION EVENT TYPES
// Based on consensus_tx_test.go WebSocket transaction streaming structure
// ============================================================================

/**
 * Transaction event data structure
 * Represents transaction data nested inside a ConsensusEvent from the transactions channel.
 * Structure matches the Go test expectations: { hash, txhash, ... }
 */
export interface TransactionEventData {
  hash?: string; // Transaction hash (primary identifier)
  txhash?: string; // Alternative transaction hash field
  vertex_hash?: string; // DAG vertex hash containing this transaction
  transaction_index?: number; // Index of transaction within vertex
  dag_depth?: number; // DAG depth when transaction was confirmed
  shard_id?: number; // Shard ID where transaction was processed
  consensus_round?: number; // Consensus round number
  code?: number; // Transaction result code
  raw_log?: string; // Raw transaction log
  info?: string; // Additional transaction info
  data?: string; // Transaction data payload
  status?: string; // Transaction status (e.g., "confirmed", "pending")
  timestamp?: Timestamp | number; // Transaction timestamp
  consensus_version?: string; // Consensus protocol version
  parent_vertices?: string[]; // Parent vertex hashes in DAG
  [key: string]: any; // Allow additional fields
}

/**
 * Transaction event structure from transactions WebSocket channel
 * Matches the structure expected by consensus_tx_test.go:
 * { event_type, data: { hash, txhash, ... }, source_shard, event_time }
 */
export interface TransactionEvent {
  event_type: string; // Event type (e.g., "new_tx", "tx_confirmed", "tx_pending")
  data: TransactionEventData; // Transaction data with hash/txhash fields
  source_shard?: string; // Shard ID where the event originated
  event_time?: number | Timestamp; // Unix timestamp of the event
  [key: string]: any; // Allow additional fields for flexibility
}

// ============================================================================
// MULTI-FEED SUB-CHANNEL TYPES
// Based on MultiFeed WebSocket API Specification v1.0
// ============================================================================

/**
 * Multi-feed message envelope structure
 * All multi-feed messages follow this common envelope format
 */
export interface MultiFeedRaweEnvelope {
  type: "data";
  channel: "multi_feed";
  sub_channel: string;
  market_id: string;
  id?: string;
  data: any;
  first_sequence_id?: number; // Binance-style batch first ID
  last_sequence_id?: number; // Binance-style batch last ID
  sequence_id?: number; // Per-message sequence ID
  checksum?: string; // CRC32 checksum (primarily orderbook)
}

/**
 * Multi-feed acknowledgment response
 */
export interface MultiFeedAck {
  type: "ack";
  channel: "multi_feed";
  id?: string;
  market_id: string;
  symbol: string;
  address?: string;
  message: string;
  sub_channels: string[];
}

/**
 * Multi-feed orderbook entry (tuple format: [price, quantity])
 * Note: Uses tuple format, not object format
 */
export type MultiFeedOrderbookEntry = [string, string];

/**
 * Multi-feed orderbook data structure
 */
export interface MultiFeedOrderbookData {
  market_id: string;
  sequence_id: string;
  first_update_id: number;
  last_update_id: number;
  checksum: string;
  bids: MultiFeedOrderbookEntry[]; // Array of [price, quantity] tuples (descending price)
  asks: MultiFeedOrderbookEntry[]; // Array of [price, quantity] tuples (ascending price)
  timestamp: string; // ISO 8601 timestamp
  snapshot?:boolean;
}

/**
 * Multi-feed trades data structure
 */
export interface MultiFeedTradesData {
  trade_id: string;
  price: string;
  quantity: string;
  side: "BUY" | "SELL";
  executed_at: Timestamp;
  timestamp: Timestamp;
}

/**
 * Multi-feed positions data structure (authenticated)
 */
export interface MultiFeedPositionsData {
  position_id?: string; // Position identifier (optional, may not be in WebSocket updates)
  market_id: string;
  address: string;
  symbol: string;
  bucket_id: string;
  size: string;
  entry_price: string;
  mark_price: string;
  unrealized_profit: string;
  unrealized_loss: string;
  margin_ratio: string;
  leverage: string;
  liquidation_price: string;
  timestamp: Timestamp;
  status: string; // "OPEN", "CLOSED", etc.
  mmr: string; // Maintenance margin ratio
  margin_balance: string;
  market_type: string; // "linear_perp", "inverse_perp", "spot"
  realized_profit: string;
  realized_loss: string;
  position_side: "LONG" | "SHORT";
  sequence_id?: number;
}

/**
 * Multi-feed orders data structure (authenticated)
 */
export interface MultiFeedOrdersData {
  address: string;
  order_id: string;
  market_id: string;
  bucket_id: string;
  symbol: string;
  status: string; // "PENDING", "FILLED", "CANCELLED", etc.
  filled_quantity: string;
  remaining_quantity: string;
  average_fill_price: string;
  reason: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed buckets data structure (authenticated)
 */
export interface MultiFeedBucketsData {
  address: string;
  buckets: MultiFeedBucketData[];
  total_buckets: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed bucket data structure
 */
export interface MultiFeedBucketData {
  bucket_id: string;
  address: string;
  margin_type: "ISOLATED" | "CROSS";
  total_equity: string;
  used_margin: string;
  available_margin: string;
  unrealized_profit: string;
  unrealized_loss: string;
}

/**
 * Multi-feed balances data structure (authenticated)
 */
export interface MultiFeedBalancesData {
  address: string;
  balances: MultiFeedBalanceData[];
  count: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed balance data structure
 */
export interface MultiFeedBalanceData {
  asset: string; // e.g., "USDC", "MORM"
  balance: string; // Total balance (wei format)
  available_balance: string; // Available balance (wei format)
  locked_balance: string; // Locked balance (wei format)
}

/**
 * Multi-feed mark price data structure
 */
export interface MultiFeedMarkPriceData {
  symbol: string;
  mark_price: string;
  index_price: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed funding rate data structure
 */
export interface MultiFeedFundingRateData {
  symbol: string;
  market_id: string;
  funding_rate: string;
  mark_price: string;
  index_price: string;
  premium_rate: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed deviation data structure
 */
export interface MultiFeedDeviationData {
  symbol: string;
  market_id: string;
  deviation_bps: string;
  deviation_percent: string;
  last_price: string;
  mark_price: string;
  index_price: string;
  direction: "contango" | "backwardation" | "par";
  timestamp: Timestamp;
}

/**
 * Multi-feed TWAP data structure
 */
export interface MultiFeedTWAPData {
  symbol: string;
  market_id: string;
  twap_value: string;
  window_size: number;
  sample_count: number;
  start_time: number; // Unix timestamp
  end_time: number; // Unix timestamp
  calculation_method: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed kline data structure
 */
export interface MultiFeedKlineData {
  symbol: string;
  interval: string; // e.g., "1m", "5m", "1h"
  open_time: number; // Unix timestamp
  close_time: number; // Unix timestamp
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  trade_count: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed last price data structure
 */
export interface MultiFeedLastPriceData {
  symbol: string;
  market_id: string;
  price: string;
  quantity: string;
  is_buyer_maker: boolean;
  trade_id: string;
  timestamp: Timestamp;
  derived: boolean; // true if derived from mark price (no trades exist)
}

/**
 * Multi-feed index price data structure
 */
export interface MultiFeedIndexPriceData {
  symbol: string; // e.g., "BTC/USD"
  weighted_average: string;
  weighted_median: string;
  confidence: string; // 0.0-1.0
  total_sources: number;
  reliable_sources: number;
  is_valid: boolean;
  timestamp: Timestamp;
}

/**
 * Multi-feed price feed data structure
 */
export interface MultiFeedPriceFeedData {
  symbol: string; // e.g., "BTC/USD"
  price: string;
  volume_24h: string;
  price_change_24h: string;
  source_exchange: string; // e.g., "Pyth", "Chainlink"
  timestamp: Timestamp;
}

/**
 * Discriminated union type for multi-feed sub-channel data
 * Each sub-channel has its own specific data structure
 */
export type MultiFeedSubChannelData =
  | { sub_channel: "orderbook"; data: MultiFeedOrderbookData }
  | { sub_channel: "trades"; data: MultiFeedTradesData }
  | { sub_channel: "positions"; data: MultiFeedPositionsData }
  | { sub_channel: "orders"; data: MultiFeedOrdersData }
  | { sub_channel: "buckets"; data: MultiFeedBucketsData }
  | { sub_channel: "balances"; data: MultiFeedBalancesData }
  | { sub_channel: "mark_price"; data: MultiFeedMarkPriceData }
  | { sub_channel: "funding_rate"; data: MultiFeedFundingRateData }
  | { sub_channel: "deviation"; data: MultiFeedDeviationData }
  | { sub_channel: "twap"; data: MultiFeedTWAPData }
  | { sub_channel: "kline"; data: MultiFeedKlineData }
  | { sub_channel: "last_price"; data: MultiFeedLastPriceData }
  | { sub_channel: "index_price"; data: MultiFeedIndexPriceData }
  | { sub_channel: "price_feed"; data: MultiFeedPriceFeedData }
  | { sub_channel: string; data: any }; // Fallback for unknown sub-channels

/**
 * Multi-feed message structure (complete message with envelope)
 */
export interface MultiFeedMessage extends MultiFeedRaweEnvelope {
  sub_channel: string;
  data: any;
}

/**
 * Type guard to check if a message is a multi-feed orderbook message
 */
export function isMultiFeedOrderbook(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "orderbook";
  data: MultiFeedOrderbookData;
} {
  return message.sub_channel === "orderbook";
}

/**
 * Type guard to check if a message is a multi-feed trades message
 */
export function isMultiFeedTrades(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "trades";
  data: MultiFeedTradesData;
} {
  return message.sub_channel === "trades";
}

/**
 * Type guard to check if a message is a multi-feed positions message
 */
export function isMultiFeedPositions(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "positions";
  data: MultiFeedPositionsData;
} {
  return message.sub_channel === "positions";
}

/**
 * Type guard to check if a message is a multi-feed orders message
 */
export function isMultiFeedOrders(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "orders";
  data: MultiFeedOrdersData;
} {
  return message.sub_channel === "orders";
}

/**
 * Type guard to check if a message is a multi-feed buckets message
 */
export function isMultiFeedBuckets(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "buckets";
  data: MultiFeedBucketsData;
} {
  return message.sub_channel === "buckets";
}

/**
 * Type guard to check if a message is a multi-feed balances message
 */
export function isMultiFeedBalances(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "balances";
  data: MultiFeedBalancesData;
} {
  return message.sub_channel === "balances";
}

/**
 * Type guard to check if a message is a multi-feed mark price message
 */
export function isMultiFeedMarkPrice(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "mark_price";
  data: MultiFeedMarkPriceData;
} {
  return message.sub_channel === "mark_price";
}

/**
 * Type guard to check if a message is a multi-feed funding rate message
 */
export function isMultiFeedFundingRate(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "funding_rate";
  data: MultiFeedFundingRateData;
} {
  return message.sub_channel === "funding_rate";
}

/**
 * Type guard to check if a message is a multi-feed deviation message
 */
export function isMultiFeedDeviation(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "deviation";
  data: MultiFeedDeviationData;
} {
  return message.sub_channel === "deviation";
}

/**
 * Type guard to check if a message is a multi-feed TWAP message
 */
export function isMultiFeedTWAP(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "twap";
  data: MultiFeedTWAPData;
} {
  return message.sub_channel === "twap";
}

/**
 * Type guard to check if a message is a multi-feed kline message
 */
export function isMultiFeedKline(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "kline";
  data: MultiFeedKlineData;
} {
  return message.sub_channel === "kline";
}

/**
 * Type guard to check if a message is a multi-feed last price message
 */
export function isMultiFeedLastPrice(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "last_price";
  data: MultiFeedLastPriceData;
} {
  return message.sub_channel === "last_price";
}

/**
 * Type guard to check if a message is a multi-feed index price message
 */
export function isMultiFeedIndexPrice(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "index_price";
  data: MultiFeedIndexPriceData;
} {
  return message.sub_channel === "index_price";
}

/**
 * Type guard to check if a message is a multi-feed price feed message
 */
export function isMultiFeedPriceFeed(
  message: MultiFeedMessage,
): message is MultiFeedRaweEnvelope & {
  sub_channel: "price_feed";
  data: MultiFeedPriceFeedData;
} {
  return message.sub_channel === "price_feed";
}
